import { Injectable } from '@angular/core';

import { QueryStringParamService } from './query-string-param-service';

@Injectable({
    providedIn: 'root'
})
export class DeepLinkProcessor {
    constructor(private readonly queryStringParamService: QueryStringParamService) { }

    private readonly identifyHandlers: IIdentifyHandler[] = [];

    /**
     * Allows external code to intercept and handle querystring parameters
     * @param parameters - an object with a list of desired parameters. ex: {'whatYouWantItCalled': ['couldBeThis','orThis'], 'param2': 'param2'}
     * @param callback - callback
     * @param requireAll - if true, your handler will only be called if all of the specified parameters are available. Defaults to false
     * @return a function that can be called to unregister
     */
    public registerHandler(parameters: { [key: string]: string | string[] }, callback: DeepLinkHandlerCallback, requireAll = false) {
        const handler = {
            callback,
            parameters,
            requireAll
        };
        this.identifyHandlers.push(handler);

        //a function that will unregister this handler
        return () => {
            const index = this.identifyHandlers.indexOf(handler);
            if (index > -1) {
                this.identifyHandlers.splice(index, 1);
            }
        };
    }

    /**
     * Call all handlers in order until all parameters are processed or all handlers have been called.
     * If previous handlers have already processed all of the parameters in your request list (or if you speicifed requireAll, then if any of your paremeters were missing), your handler will not be called.
     * This should only be called once per page-load.
     */
    public async process(search?: string) {
        const queryParams = this.queryStringParamService.getQueryStringParams(search);
        for (const handler of this.identifyHandlers) {
            //if there are no more query parameters to process, stop here
            if (this.isEmpty(queryParams)) {
                break;
            }

            const handlerParameters = this.getParameters(handler.parameters, queryParams);

            if (Object.keys(handlerParameters.parameters).length > 0) {
                let shouldCallHandler = true;
                //if the handler requires that all parameters be present, verify that is true
                if (handler.requireAll) {
                    const paramNames = Object.keys(handler.parameters);
                    inner: for (const paramName of paramNames) {
                        if (handlerParameters.parameters[paramName] === undefined) {
                            shouldCallHandler = false;
                            break inner;
                        }
                    }
                }

                if (shouldCallHandler) {
                    //at this point we have all of the parameters that the handler requested. Call the handler
                    const shouldDeleteParams = await handler.callback(handlerParameters.parameters);
                    //if the callback explicitly returned anything other than false,
                    //then remove those params, since only one handler gets to process a given param
                    if (shouldDeleteParams !== false) {
                        this.removeParams(handlerParameters.parameterNames, queryParams);
                    }
                }
            }
        }
    }

    public getParameters(parameters: any, queryParams: any) {
        const handlerParameters = { parameters: { }, parameterNames: [] };

        for (const key of Object.keys(parameters)) {
            //if the param names is a string, convert it to an array. otherwise it's already in the proper format
            const paramNames = (Array.isArray(parameters[key]) ? parameters[key] : [parameters[key]]) as string[];
            handlerParameters.parameterNames.push.apply(handlerParameters.parameterNames, paramNames);
            const value = this.getAnyParam(paramNames, queryParams);
            if (value !== undefined) {
                handlerParameters.parameters[key] = value;
            }
        }

        return handlerParameters;
    }

    /**
     * Remove all parameters with the specified names
     */
    private removeParams(paramNames: string[], queryParams: {}) {
        for (let paramName of paramNames) {
            paramName = (paramName + '').toLowerCase();
            if (queryParams[paramName]) {
                queryParams[paramName] = null;
            }
        }
    }

    /**
     * Given a list of param names, find the first defined value
     */
    private getAnyParam(paramNames: string[], queryParams) {
        for (let paramName of paramNames) {
            paramName = (paramName + '').toLowerCase();
            if (queryParams[paramName] !== undefined) {
                return queryParams[paramName];
            }
        }
    }

    private readonly isEmpty = obj => [Object, Array].includes((obj || {}).constructor) && !Object.entries((obj || {})).length;
}

export type DeepLinkHandlerCallback = (parameters: { [key: string]: string }) => boolean | void | Promise<boolean> | Promise<void>;

interface IIdentifyHandler {
    callback: DeepLinkHandlerCallback;
    parameters: { [key: string]: string | string[] };
    requireAll: boolean;
}
